import { Component, ElementRef, Input, OnInit, Renderer2, ViewChild, ViewContainerRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ButtonEditRendererComponent } from 'src/app/components/button-edit-render.components';
import { FormViewerInterface } from 'src/app/interface/form-viewer.interface';
import { DropdownOperationService } from 'src/app/services/dropdown-operation.service';
import { FormBuilderUDFService } from 'src/app/services/form-builder-udf-service';
import { ListOperationService } from 'src/app/services/list-operation.service';
import { SharedOperationService } from 'src/app/services/shared-operation.service';
import { TranslaterService } from 'src/app/services/translater.service';
import { UserManagementService } from 'src/app/services/usermanagement.service';
import { BaseApi } from 'src/backend/api/base-api';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

@Component({
  selector: 'app-form-viewer',
  templateUrl: './form-viewer.component.html',
  styleUrls: ['./form-viewer.component.scss']
})
export class FormViewerComponent implements OnInit {
  @ViewChild('contentToConvert', { static: false }) contentToConvert!: ElementRef;
  formHtml!: SafeHtml;
  private formHtmlLoaded = false;
  breadcrumList: any;
  languageDir = 'ltr';
  langObj: any = {};
  moduleId!: any;
  pageId!: any;
  lang: any;
  jsonData: any;
  dataObj: any;
  columnDefs: any[] = [];
  rowData: any[] = [];
  frameworkComponents: any;
  idOnEdit: any = '';
  plantSession: any;
  groupSession: any;
  private listener: () => void;
  pageName: string = '';

  constructor(
    private formService: FormBuilderUDFService,
    private sanitizer: DomSanitizer,
    private el: ElementRef,
    private renderer: Renderer2,
    private translate: TranslateService,
    private router: Router,
    private usermanagementService: UserManagementService,
    private translater: TranslaterService,
    private toaster: ToastrService,
    private baseApi: BaseApi,
    private dropdownService: DropdownOperationService,
    private listOperationService: ListOperationService,
    private sharedService: SharedOperationService,
    private spinner: NgxSpinnerService
  ) {
    this.frameworkComponents = {
      editButtonRenderer: ButtonEditRendererComponent
    };
    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      event.lang === 'ar' ? this.languageDir = 'rtl' : this.languageDir = 'ltr';
      this.getLangWords();
    });
  }

  ngOnInit(): void {
    this.plantSession = this.baseApi.getPlantFromStorage();
    this.groupSession = this.baseApi.getGroupFromStorage();
    localStorage.removeItem('PageInfo');
    localStorage.removeItem('rowId');
    this.getAllForms();
  }

  /** For tests to mock html2canvas */
  protected getHtml2Canvas(): typeof html2canvas { return html2canvas; }
  /** For tests to mock jsPDF */
  protected getJsPDF(): typeof jsPDF { return jsPDF; }

  exportAsPDF(): Promise<void> {
    let createdTemp = false;
    if (!this.contentToConvert) {
      console.warn('contentToConvert missing - creating temporary container for export');
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = '<div class="pdf-temp"><h1>Temp Report</h1><p>No runtime content - test fallback.</p></div>';
      this.contentToConvert = { nativeElement: tempDiv } as any;
      createdTemp = true;
    }
    const DATA = this.contentToConvert.nativeElement as HTMLElement;
    this.spinner.show();
    const { headers: preHeaders, rows: preRows } = this.prepareGridTableData(DATA);
    const exportZoom = 1.75;
    const zoomCleanup = this.applyZoomForCapture(DATA, exportZoom);
    const replacementContext = this.injectTemporaryTables(DATA, preHeaders, preRows);
    const html2CanvasFn: any = (window as any).html2canvas || this.getHtml2Canvas();
    const JsPDFCtor: any = (window as any).jsPDF || this.getJsPDF();
    return html2CanvasFn(DATA, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      ignoreElements: (el: HTMLElement) => {
        const tag = el.tagName?.toLowerCase();
        if (!tag) return false;
        if (tag === 'button') return true;
        if (tag === 'input') {
          const type = (el.getAttribute('type') || '').toLowerCase();
          if (['button', 'submit', 'reset'].includes(type)) return true;
        }
        if (tag === 'a' && (el.getAttribute('role') === 'button' || el.classList.contains('btn'))) return true;
        if (el.classList.contains('pdf-exclude') || el.getAttribute('data-pdf-exclude') === 'true') return true;
        return false;
      }
    })
      .then((canvas: any) => {
        const pdf = new JsPDFCtor('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 12;
        const canvasWidth = canvas.width || 800;
        const canvasHeight = canvas.height || 600;
        const imgWidth = pageWidth - 2 * margin;
        const imgHeight = (canvasHeight * imgWidth) / canvasWidth;
        const headerY = 15;
        const imgStartY = headerY + 10;
        const imgData = canvas.toDataURL('image/png');
        if (pdf.setFont) pdf.setFont('helvetica', 'bold');
        if (pdf.setFontSize) pdf.setFontSize(16);
        pdf.text(this.pageName || 'Report', pageWidth / 2, headerY, { align: 'center' });
        pdf.addImage(imgData, 'PNG', margin, imgStartY, imgWidth, imgHeight);
        const totalPages = pdf.getNumberOfPages ? pdf.getNumberOfPages() : 1;
        const today = new Date().toLocaleString();
        for (let i = 1; i <= totalPages; i++) {
          if (pdf.setPage) pdf.setPage(i);
          if (pdf.setFontSize) pdf.setFontSize(9);
          pdf.text(`Page ${i}/${totalPages}`, pageWidth - margin - 25, pageHeight - 6);
          if (i === totalPages) {
            pdf.text(`Generated ${today}`, margin, pageHeight - 6);
          }
        }
        const filename = `${(this.dataObj?.form_name || 'Report').replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`;
        pdf.save(filename);
      })
      .catch((err: any) => {
        console.error('PDF export failed', err);
        this.toaster.error('Error', 'PDF export failed');
      })
      .finally(() => {
        zoomCleanup();
        this.removeTemporaryTables(replacementContext);
        this.spinner.hide();
        if (createdTemp) {
          (this.contentToConvert as any) = undefined;
        }
      });
  }

  ngAfterViewChecked() {
    if (this.formHtml && !this.formHtmlLoaded) {
      this.addEventListeners();
      this.formHtmlLoaded = true;
    }
  }

  public localizeHeader(parameters: any) {
    let headerIdentifier = parameters.colDef.field;
    return this.getTransalatedValue(headerIdentifier);
  }

  getTransalatedValue(value: string) {
    let val;
    if (this.langObj && this.langObj[value]) {
      val = this.langObj[value];
    }
    return val ? val : value;
  }

  public getLangWords() {
    const key = {
      ModuleId: this.moduleId,
      PageId: this.pageId,
      LanguageId: this.translate.currentLang === 'en' ? 1
        : this.translate.currentLang === 'ar' ? 2
        : this.translate.currentLang === 'fr' ? 3
        : this.translate.currentLang === 'sp' ? 4
        : this.translate.currentLang === 'gr' ? 5
        : this.translate.currentLang === 'du' ? 6 : 1,
    };
    this.translater.getLangWords(key).subscribe((res: any) => {
      let obj: any = {};
      res.tagList.map((elem: any) => {
        obj[elem.tagName] = elem.translatedValue;
      });
      this.langObj = obj;
    });
  }

  getAllForms() {
    this.pageId = this.baseApi.getCurrentMenuID();
    const key = {
      pageId: this.pageId,
      plantCode: this.plantSession?.plantCode,
    };
    this.spinner.show();
    this.formService.getFormByModulePage(key).subscribe({
      next: (data: any) => {
        if (!data?.Table || data.Table.length === 0) {
          this.spinner.hide();
          return;
        }
        this.dataObj = data?.Table[0];
        this.pageName = data?.Table[0]?.PageText;
        const htmlString = data?.Table[0]?.form_source_code_html;
        this.jsonData = JSON.parse(data?.Table[0]?.form_source_code_json);
        this.formHtml = this.sanitizer.bypassSecurityTrustHtml(htmlString);

        const formViewerData: FormViewerInterface = {
          json_data: this.jsonData,
          html_data: this.formHtml,
          container: this.el,
          renderer: this.renderer,
          onEditCallback: (submissionId: number) => {        
            this.loadFormDataForEdit(submissionId);
          }
        };

        localStorage.setItem('PageInfo', this.dataObj.form_name.replace(/\s+/g, '') + (this.dataObj?.id || ''));
        this.sharedService.setFormViewerData(formViewerData);
        setTimeout(() => this.sharedService.initializePlugins(), 0);
        setTimeout(() => {
          this.dropdownService.loadAndPopulateComponents(this.jsonData, this.el, this.renderer);
        }, 0);
        this.spinner.hide();
      },
      error: (error: any) => {
        console.error('Error fetching form HTML:', error);
        this.spinner.hide();
      }
    });
  }

  // ─── LOAD FORM DATA FOR EDIT ──────────────────────────────────────────────
  loadFormDataForEdit(submissionId: number): void {
    const formId = this.dataObj.form_name.replace(/\s+/g, '') + (this.dataObj?.id || '');

    const payload = {
      formId: formId,
      submissionId: submissionId
    };

    this.spinner.show();
    this.formService.GetFormDataById(payload).subscribe({
      next: (response: any) => {
        const record = response?.Table?.[0];
        if (!record) {
          this.spinner.hide();
          return;
        }

        // ── PATCH FORM FIELDS ────────────────────────────────────────────────
        const formElement = this.el.nativeElement.querySelector('form');
        if (formElement) {
          const inputs = formElement.querySelectorAll('input, select, textarea');
          inputs.forEach((input: any) => {
            const key = input.name;
            if (!key || !(key in record)) return;

            switch (input.type) {
              case 'radio':
                input.checked = input.value === String(record[key]);
                break;

              case 'checkbox':
                if (typeof record[key] === 'string') {
                  input.checked = record[key].toLowerCase() === 'true' || record[key] === '1';
                } else {
                  input.checked = Boolean(record[key]);
                }
                break;

              case 'date':
                input.value = record[key]
                  ? new Date(record[key]).toISOString().split('T')[0]
                  : '';
                break;

              default:
                if (input.tagName === 'SELECT') {
                  // Parse stored {value, text} JSON if needed
                  let val = record[key];
                  if (typeof val === 'string' && val.trim().startsWith('{')) {
                    try {
                      const parsed = JSON.parse(val);
                      val = parsed.value || val;
                    } catch { }
                  }
                  input.value = val || '';
                  // Trigger dependent dropdowns after patching
                  setTimeout(() => {
                    this.dropdownService.onDropdownChange(input.name, this.el, this.renderer);
                  }, 0);
                } else {
                  input.value = record[key] ?? '';
                }
                break;
            }
          });
        }

        // ── CHECK IF FORM HAS LIST-AS-INPUT ──────────────────────────────────
        const listComponents: any[] = [];
        this.jsonData?.added_components?.forEach((component: any) => {
          if (component.addedComponents) {
            component.addedComponents.forEach((innerComponents: any[]) => {
              innerComponents.forEach((innerComponent: any) => {
                if (
                  innerComponent.elementName === 'list' &&
                  innerComponent.element?.listtype === 'inputlist'
                ) {
                  listComponents.push(innerComponent);
                }
              });
            });
          }
        });

        // ── CASE 1: Simple Form — no list components ─────────────────────────
        if (!listComponents.length) {
          this.spinner.hide();
          return;
        }

        // ── CASE 2: Form + List as Input ─────────────────────────────────────
        const listRequests = listComponents.map((listComponent) => {
          const listPayload = {
            formId: formId,
            fieldName: listComponent.element.field_name,
            submissionId: submissionId
          };

          return this.formService.GetFormListData(listPayload).toPromise().then((listResponse: any) => {
            const listRows = listResponse?.Table || [];
            if (!listRows.length) return;

            // Rebuild rows from unpivoted data
            // listRows: [{SubmissionID, RowIndex, ColumnName, ColumnValue}]
            const rowMap: { [rowIndex: string]: any } = {};
            listRows.forEach((cell: any) => {
              if (!rowMap[cell.RowIndex]) {
                rowMap[cell.RowIndex] = {};
              }
              rowMap[cell.RowIndex][cell.ColumnName] = cell.ColumnValue;
            });

            const rebuiltRows = Object.values(rowMap);

            // Push rebuilt rows into ag-grid
            const gridApi = this.listOperationService.getGridInstance(
              listComponent.element.field_name
            );

            if (gridApi) {
              gridApi.setRowData(rebuiltRows);
            } else {
              // Grid not ready — retry after short delay
              setTimeout(() => {
                const retryApi = this.listOperationService.getGridInstance(
                  listComponent.element.field_name
                );
                if (retryApi) {
                  retryApi.setRowData(rebuiltRows);
                } else {
                  console.warn('Grid instance not found for:', listComponent.element.field_name);
                }
              }, 500);
            }
          }).catch((err: any) => {
            console.error('Error fetching list data for:', listComponent.element.field_name, err);
          });
        });

        Promise.all(listRequests).finally(() => {
          this.spinner.hide();
        });
      },
      error: (error: any) => {
        console.error('Error loading form data for edit:', error);
        this.toaster.error('Error', 'Failed to load record for editing');
        this.spinner.hide();
      }
    });
  }

  ngOnDestroy() {
    if (this.listener) this.listener();
  }

  addEventListeners() {
    const container = this.el.nativeElement;
    this.listener = this.renderer.listen(container, 'click', (event: Event) => {
      const button = (event.target as HTMLElement).closest('button');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      const buttonType = button.getAttribute('type')?.toLowerCase();

      switch (buttonType) {
        case 'save':
          this.onSubmit();
          break;
        case 'search':
          this.onSearch();
          break;
        case 'reset':
          this.reset();
          break;
        case 'export as pdf':
          this.exportAsPDF();
          break;
        case 'partial save':
          this.onPartialSave();
          break;
        default:
          console.warn('Unknown button type:', buttonType);
      }
    });
  }

  onSubmit() {
    this.idOnEdit = localStorage.getItem('rowId');
    if (this.idOnEdit) {
      this.updateFormData();
    } else {
      const formDataValues = this.sharedService.getFormValues(this.el);

      let gridData = {};
      const hasListAsInput = this.jsonData?.added_components?.some((component: any) => {
        if (component.addedComponents) {
          return component.addedComponents.some((innerComponents: any[]) =>
            innerComponents.some((innerComponent: any) =>
              innerComponent.elementName === 'list' &&
              innerComponent.element?.listtype === 'inputlist'
            )
          );
        }
        return false;
      });

      if (hasListAsInput) {
        gridData = this.listOperationService.getGridData();
      }

      const combinedData = { ...formDataValues, ...gridData };

      const formEl: HTMLFormElement | null = this.el.nativeElement.querySelector('form');
      if (formEl) {
        if (formEl.checkValidity() === false) {
          this.toaster.error('Error', 'Please fill all required fields');
          return;
        }
        const spaceOnlyFields = Array.from(
          formEl.querySelectorAll('input[type="text"],input[type="number"],input[type="password"],input[type="email"],textarea')
        ).filter((el: any) => el.value.length > 0 && el.value.trim().length === 0);

        if (spaceOnlyFields.length) {
          this.toaster.error('Error', 'Fields cannot contain only spaces');
          (spaceOnlyFields[0] as HTMLElement).focus();
          return;
        }
      }

      const formData: any = {
        formId: this.dataObj.form_name.replace(/\s+/g, '') + (this.dataObj?.id || ''),
        PageCode: this.pageId,
        SubmissionStatus: 'Submitted',
        Values: combinedData
      };

      this.spinner.show();
      this.formService.InsertFormData(formData).subscribe({
        next: (response: any) => {
          this.toaster.success('Success', 'Data Has Been Saved Successfully');
          localStorage.removeItem('rowId');
          this.reset();
          this.fetchGridData();
          this.spinner.hide();
        },
        error: (error: any) => {
          console.error('Error saving form data:', error);
          this.spinner.hide();
        }
      });
    }
  }

  updateFormData() {
    const formDataValues = this.sharedService.getFormValues(this.el);

    const formEl: HTMLFormElement | null = this.el.nativeElement.querySelector('form');
    if (formEl) {
      if (formEl.checkValidity() === false) {
        this.toaster.error('Error', 'Please fill all required fields');
        return;
      }
      const spaceOnlyFields = Array.from(
        formEl.querySelectorAll('input[type="text"],input[type="number"],input[type="password"],input[type="email"],textarea')
      ).filter((el: any) => el.value.length > 0 && el.value.trim().length === 0);

      if (spaceOnlyFields.length) {
        this.toaster.error('Error', 'Fields cannot contain only spaces');
        (spaceOnlyFields[0] as HTMLElement).focus();
        return;
      }
    }

    // Include grid data for list-as-input on update too
    let gridData = {};
    const hasListAsInput = this.jsonData?.added_components?.some((component: any) => {
      if (component.addedComponents) {
        return component.addedComponents.some((innerComponents: any[]) =>
          innerComponents.some((innerComponent: any) =>
            innerComponent.elementName === 'list' &&
            innerComponent.element?.listtype === 'inputlist'
          )
        );
      }
      return false;
    });

    if (hasListAsInput) {
      gridData = this.listOperationService.getGridData();
    }

    const combinedData = { ...formDataValues, ...gridData };

    const formData: any = {
      formId: this.dataObj.form_name.replace(/\s+/g, '') + (this.dataObj?.id || ''),
      SubmissionID: parseInt(this.idOnEdit),
      SubmissionStatus: 'Submitted',
      Values: combinedData
    };

    this.spinner.show();
    this.formService.updateFormData(formData).subscribe({
      next: (response: any) => {
        this.toaster.success('Success', 'Data Has Been Updated Successfully');
        localStorage.removeItem('rowId');
        this.fetchGridData();
        this.reset();
        this.spinner.hide();
      },
      error: (error: any) => {
        console.error('Error updating form data:', error);
        this.spinner.hide();
      }
    });
  }

  onPartialSave() {
    this.idOnEdit = localStorage.getItem('rowId');

    if (this.idOnEdit) {
      this.updatePartialFormData();
    } else {
      const formDataValues = this.sharedService.getFormValues(this.el);

      let gridData = {};
      const hasListAsInput = this.jsonData?.added_components?.some((component: any) => {
        if (component.addedComponents) {
          return component.addedComponents.some((innerComponents: any[]) =>
            innerComponents.some((innerComponent: any) =>
              innerComponent.elementName === 'list' &&
              innerComponent.element?.listtype === 'inputlist'
            )
          );
        }
        return false;
      });

      if (hasListAsInput) {
        gridData = this.listOperationService.getGridData();
      }

      const combinedData = { ...formDataValues, ...gridData };
       const formEl: HTMLFormElement | null = this.el.nativeElement.querySelector('form');
      if (formEl) {
        if (formEl.checkValidity() === false) {
          this.toaster.error('Error', 'Please fill all required fields');
          return;
        }
        const spaceOnlyFields = Array.from(
          formEl.querySelectorAll('input[type="text"],input[type="number"],input[type="password"],input[type="email"],textarea')
        ).filter((el: any) => el.value.length > 0 && el.value.trim().length === 0);

        if (spaceOnlyFields.length) {
          this.toaster.error('Error', 'Fields cannot contain only spaces');
          (spaceOnlyFields[0] as HTMLElement).focus();
          return;
        }
      }

      const formData: any = {
        formId: this.dataObj.form_name.replace(/\s+/g, '') + (this.dataObj?.id || ''),
        PageCode: this.pageId,
        SubmissionStatus: 'Draft',
        Values: combinedData
      };

      this.spinner.show();
      this.formService.InsertFormData(formData).subscribe({
        next: (response: any) => {
          this.toaster.success('Success', 'Data Has Been Partially Saved');
          if (response?.Table?.[0]?.SubmissionID) {
            localStorage.setItem('rowId', response.Table[0].SubmissionID);
          }
          this.reset();
          this.listOperationService.resetInputLists(this.el.nativeElement);
          this.fetchGridData();
          this.spinner.hide();
        },
        error: (error: any) => {
          console.error('Error partially saving form data:', error);
          this.toaster.error('Error', 'Partial save failed');
          this.spinner.hide();
        }
      });
    }
  }

  updatePartialFormData() {
    const formDataValues = this.sharedService.getFormValues(this.el);

    let gridData = {};
    const hasListAsInput = this.jsonData?.added_components?.some((component: any) => {
      if (component.addedComponents) {
        return component.addedComponents.some((innerComponents: any[]) =>
          innerComponents.some((innerComponent: any) =>
            innerComponent.elementName === 'list' &&
            innerComponent.element?.listtype === 'inputlist'
          )
        );
      }
      return false;
    });

    if (hasListAsInput) {
      gridData = this.listOperationService.getGridData();
    }

    const combinedData = { ...formDataValues, ...gridData };
      const formEl: HTMLFormElement | null = this.el.nativeElement.querySelector('form');
      if (formEl) {
        if (formEl.checkValidity() === false) {
          this.toaster.error('Error', 'Please fill all required fields');
          return;
        }
        const spaceOnlyFields = Array.from(
          formEl.querySelectorAll('input[type="text"],input[type="number"],input[type="password"],input[type="email"],textarea')
        ).filter((el: any) => el.value.length > 0 && el.value.trim().length === 0);

        if (spaceOnlyFields.length) {
          this.toaster.error('Error', 'Fields cannot contain only spaces');
          (spaceOnlyFields[0] as HTMLElement).focus();
          return;
        }
      }

    const formData: any = {
      formId: this.dataObj.form_name.replace(/\s+/g, '') + (this.dataObj?.id || ''),
      SubmissionID: parseInt(this.idOnEdit),
      SubmissionStatus: 'Draft',
      Values: combinedData
    };

    this.spinner.show();
    this.formService.updateFormData(formData).subscribe({
      next: (response: any) => {
        this.toaster.success('Success', 'Draft Updated Successfully');
        this.reset();
        this.listOperationService.resetInputLists(this.el.nativeElement);
        this.fetchGridData();
        this.spinner.hide();
      },
      error: (error: any) => {
        console.error('Error updating partial save:', error);
        this.toaster.error('Error', 'Draft update failed');
        this.spinner.hide();
      }
    });
  }

  onSearch() {
    this.fetchGridData();
  }

  reset() {
    const formElement = this.el.nativeElement.querySelector('form');
    if (formElement) {
      const inputs = formElement.querySelectorAll('input, select, textarea');
      inputs.forEach((input: any) => {
        if (input.type === 'radio' || input.type === 'checkbox') {
          input.checked = false;
        } else {
          input.value = '';
        }
      });
    }
    localStorage.removeItem('rowId');
    this.idOnEdit = '';
  }

  fetchGridData() {
    for (const component of this.jsonData.added_components) {
      if (component.addedComponents) {
        for (const innerComponents of component.addedComponents) {
          for (const innerComponent of innerComponents) {
            if (
              innerComponent.elementName === 'list' &&
              innerComponent.element.datasourceUrl &&
              innerComponent.element?.listtype !== 'inputlist'
            ) {
              this.listOperationService.createListGrid(innerComponent, this.el.nativeElement);
            }
          }
        }
      }
    }
  }

  private extractAgGridData(root: HTMLElement): { headers: string[]; rows: string[][] } {
    const headers: string[] = [];
    const rows: string[][] = [];
    const headerCells = root.querySelectorAll('.ag-header .ag-header-cell .ag-header-cell-text');
    headerCells.forEach(h => headers.push(((h as HTMLElement).textContent || '').trim()));
    const rowEls = root.querySelectorAll('.ag-center-cols-container .ag-row');
    rowEls.forEach(rowEl => {
      const cellValues: string[] = [];
      const cellEls = rowEl.querySelectorAll('.ag-cell');
      cellEls.forEach(c => cellValues.push(((c as HTMLElement).textContent || '').trim()));
      if (cellValues.length) rows.push(cellValues);
    });
    return { headers, rows };
  }

  private prepareGridTableData(root: HTMLElement): { headers: string[]; rows: string[][] } {
    if (this.columnDefs && this.columnDefs.length && this.rowData && this.rowData.length) {
      const headers = this.columnDefs.map(c => c.headerName || c.field || '');
      const rows = this.rowData.map(r => this.columnDefs.map(c => (r[c.field] !== undefined && r[c.field] !== null) ? String(r[c.field]) : ''));
      return { headers, rows };
    }
    return this.extractAgGridData(root);
  }

  private injectTemporaryTables(root: HTMLElement, headers: string[], rows: string[][]) {
    const gridWrappers = Array.from(root.querySelectorAll('.ag-root, .ag-root-wrapper')) as HTMLElement[];
    const context: { wrapper: HTMLElement; placeholder: HTMLElement; originalDisplay: string | null }[] = [];
    if (!headers.length || !rows.length) return context;
    gridWrappers.forEach(wrapper => {
      const table = document.createElement('table');
      table.className = 'pdf-temp-table';
      table.style.width = '100%';
      table.style.borderCollapse = 'collapse';
      table.style.fontSize = '12px';
      table.style.margin = '8px 0';
      const thead = document.createElement('thead');
      const headRow = document.createElement('tr');
      headers.forEach(h => {
        const th = document.createElement('th');
        th.textContent = h;
        th.style.border = '1px solid #4837dfff';
        th.style.padding = '4px 6px';
        th.style.background = '#a9a9f7ff';
        th.style.color = '#3b38f1ff';
        headRow.appendChild(th);
      });
      thead.appendChild(headRow);
      table.appendChild(thead);
      const tbody = document.createElement('tbody');
      rows.forEach(r => {
        const tr = document.createElement('tr');
        r.forEach(cell => {
          const td = document.createElement('td');
          td.textContent = cell;
          td.style.border = '1px solid #ccccccff';
          td.style.padding = '3px 5px';
          tr.appendChild(td);
        });
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);
      const placeholder = document.createElement('div');
      placeholder.className = 'pdf-temp-table-wrapper';
      placeholder.appendChild(table);
      const originalDisplay = wrapper.getAttribute('style');
      context.push({ wrapper, placeholder, originalDisplay });
      wrapper.style.display = 'none';
      wrapper.parentNode?.insertBefore(placeholder, wrapper.nextSibling);
    });
    return context;
  }

  private removeTemporaryTables(context: { wrapper: HTMLElement; placeholder: HTMLElement; originalDisplay: string | null }[]) {
    context.forEach(item => {
      item.placeholder.remove();
      if (item.originalDisplay) {
        item.wrapper.setAttribute('style', item.originalDisplay);
      } else {
        item.wrapper.removeAttribute('style');
      }
      item.wrapper.style.display = '';
    });
  }

  private applyZoomForCapture(root: HTMLElement, zoom: number): () => void {
    if (!zoom || zoom === 1) return () => { };
    const originalTransform = root.style.transform;
    const originalTransformOrigin = root.style.transformOrigin;
    const originalWidth = root.style.width;
    root.style.transform = `scale(${zoom})`;
    root.style.transformOrigin = 'top left';
    root.style.width = (100 / zoom) + '%';
    return () => {
      root.style.transform = originalTransform;
      root.style.transformOrigin = originalTransformOrigin;
      root.style.width = originalWidth;
    };
  }
}